import type { GoogleMapsApi } from '../types/googleMaps';

const GOOGLE_MAPS_SCRIPT_ID = 'washday-google-maps-script';
const GOOGLE_MAPS_CALLBACK_NAME = '__washdayGoogleMapsInit';

let googleMapsPromise: Promise<GoogleMapsApi> | null = null;

function buildGoogleMapsSrc(apiKey: string) {
  const params = new URLSearchParams({
    key: apiKey,
    loading: 'async',
    callback: GOOGLE_MAPS_CALLBACK_NAME,
    v: 'weekly',
  });

  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
}

export function getGoogleMapsApiKey() {
  return import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() || '';
}

export function isGoogleMapsApiConfigured() {
  return Boolean(getGoogleMapsApiKey());
}

export function loadGoogleMapsApi(): Promise<GoogleMapsApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser.'));
  }

  if (typeof window.google?.maps?.importLibrary === 'function') {
    return Promise.resolve(window.google);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_API_KEY is not configured.'));
  }

  googleMapsPromise = new Promise<GoogleMapsApi>((resolve, reject) => {
    const cleanup = (script?: HTMLScriptElement | null) => {
      if (window[GOOGLE_MAPS_CALLBACK_NAME as keyof Window] === handleLoaded) {
        delete window[GOOGLE_MAPS_CALLBACK_NAME as keyof Window];
      }
      script?.removeEventListener('error', handleError);
    };

    const handleLoaded = () => {
      const existingScript = document.getElementById(
        GOOGLE_MAPS_SCRIPT_ID,
      ) as HTMLScriptElement | null;
      cleanup(existingScript);

      if (typeof window.google?.maps?.importLibrary === 'function') {
        resolve(window.google);
        return;
      }

      googleMapsPromise = null;
      reject(new Error('Google Maps failed to initialize.'));
    };

    const handleError = () => {
      const existingScript = document.getElementById(
        GOOGLE_MAPS_SCRIPT_ID,
      ) as HTMLScriptElement | null;
      cleanup(existingScript);
      googleMapsPromise = null;
      reject(new Error('Google Maps failed to load.'));
    };

    window[GOOGLE_MAPS_CALLBACK_NAME as keyof Window] = handleLoaded as never;

    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = buildGoogleMapsSrc(apiKey);
    script.async = true;
    script.defer = true;
    script.addEventListener('error', handleError, { once: true });

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export async function importGoogleMapsLibrary<T>(libraryName: string) {
  const googleMaps = await loadGoogleMapsApi();

  if (typeof googleMaps.maps.importLibrary !== 'function') {
    throw new Error('Google Maps importLibrary is unavailable.');
  }

  return (await googleMaps.maps.importLibrary(libraryName)) as T;
}
